import React, { useEffect, useRef, useState } from "react";
import {
  Backdrop,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import styles from "../../../styles/misreports/misreport.module.css";
import BarChart from "../../../Components/GraphNCharts/BarChart";
import PieChart from "../../../Components/GraphNCharts/Pie";
import CustomTable from "../../../Components/MisReport/Table";
import companyLogo from "../../../assets/images/nirantara_logo_new.svg";
import { useUser } from "../../../context/AuthContext";
import { useParams, useLocation } from "react-router-dom";
import { get } from "../../../utils/api";
import { truncateNumber } from "../../../utils/common";
import { api } from "../../../utils/api";

const GhgReport = () => {
  const PageBreak = () => (
    <div
      className={styles.page_break}
      style={{ pageBreakBefore: "always", breakBefore: "page" }}
    />
  );

  const reportRef = useRef();
  const { user } = useUser();
  const { id } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const reportYear = searchParams.get("year");
  const [loading, setLoading] = useState(false); // State to track loading

  const [data, setData] = useState({});
  const [scope1BarChartData, setScope1BarChartData] = useState([]);
  const [scope2BarChartData, setScope2BarChartData] = useState([]);
  const [scope3BarChartData, setScope3BarChartData] = useState([]);
  const [summary, setSummary] = useState({
    scope1: "",
    scope2: "",
    scope3: "",
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [waterData, setWaterData] = useState([]);

  const getData = async () => {
    if (id) {
      try {
        setLoading(true);
        const response = await get(`/ghg-report/data?id=${id}`);
        setData(response?.data?.data);
      } catch (error) {
        console.log("error", error);
      } finally {
        setLoading(false); // Stop loading after response (success or error)
      }
    }
  };

  const getGHGFacilityWiseData = async () => {
    try {
      const response = await get(`/ghg-report/ghg?id=${id}`);
      setScope1BarChartData(response?.data?.scope1);
      setScope2BarChartData(response?.data?.scope2);
      setScope3BarChartData(response?.data?.scope3);
      setSummary({
        scope1: response?.data?.scope1Summary,
        scope2: response?.data?.scope2Summary,
        scope3: response?.data?.scope3Summary,
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    getData();
    getGHGFacilityWiseData();
  }, []);

  // Get current and previous year for table headers
  const currentYear = data?.reportingYear || new Date().getFullYear();
  const previousYear = Number(currentYear) - 1;
  const currentYearLabel = `${currentYear} Inventory Year (tCO2e)`;
  const previousYearLabel = `${previousYear} Inventory Year (tCO2e)`;

  const scope1Headers = [
    "Emission Source",
    currentYearLabel,
    previousYearLabel,
    "% Change",
  ];

  const scope1Rows = [
    {
      "Emission Source": "Stationary Combustion",
      [currentYearLabel]: truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.stationaryCombustion?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Mobile Combustion",
      [currentYearLabel]: truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.mobileCombustion?.percentageChange ?? 0,
      ),
    },
    {
      "Emission Source": "Fugitive Emission",
      [currentYearLabel]: truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.fugitiveCombustion?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 1 Emission",
      [currentYearLabel]: truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope1Emissions?.totalScope1Combustion?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  const scope2Rows = [
    {
      "Emission Source": "Electricity",
      [currentYearLabel]: truncateNumber(
        data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope2Emissions?.electricity?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.electricity?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Heating and Cooling",
      [currentYearLabel]: truncateNumber(
        data?.scope2Emissions?.heat?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope2Emissions?.heat?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.heat?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Steam",
      [currentYearLabel]: truncateNumber(
        data?.scope2Emissions?.steam?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope2Emissions?.steam?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.steam?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 2 Emission",
      [currentYearLabel]: truncateNumber(
        data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope2Emissions?.totalEmission?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope2Emissions?.totalEmission?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  const scope3Rows = [
    {
      "Emission Source": "Purchased Goods & Services",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]:
        truncateNumber(
          data?.scope3Emissions?.purchasedGoods?.previousMonthTotal,
        ) ?? 0,
      "% Change": truncateNumber(
        data?.scope3Emissions?.purchasedGoods?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Upstream/Downstream Transportation & Distribution",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.upstreamAndDownStream?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Waste Generated in Operations",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.wasteGenerated?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Employee Commute",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.employeeCommute?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope3Emissions?.employeeCommute?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.employeeCommute?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Business Travel",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.businessTravel?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope3Emissions?.businessTravel?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.businessTravel?.percentageChange ?? 0,
        2,
      ),
    },
    {
      "Emission Source": "Total Scope 3 Emission",
      [currentYearLabel]: truncateNumber(
        data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0,
      ),
      [previousYearLabel]: truncateNumber(
        data?.scope3Emissions?.totalEmission?.previousMonthTotal ?? 0,
      ),
      "% Change": truncateNumber(
        data?.scope3Emissions?.totalEmission?.percentageChange ?? 0,
        2,
      ),
    },
  ];

  let scope1Data = [
    {
      label: "Current Year",
      data: scope1BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      // borderWidth: 1,
      barThickness: 30,
    },
    {
      label: "Previous Year",
      data: scope1BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      // borderWidth: 1,
      barThickness: 30,
    },
  ];

  let scope2Data = [
    {
      label: "Current Year",
      data: scope2BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Year",
      data: scope2BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  let scope3Data = [
    {
      label: "Current Year",
      data: scope3BarChartData?.map((item) => item.thisYearEmission) ?? [],
      backgroundColor: "rgba(255, 79, 79, 0.8)",
      borderColor: "rgba(255, 79, 79, 1)",
      borderWidth: 1,
    },
    {
      label: "Previous Year",
      data: scope3BarChartData?.map((item) => item.previousEmission) ?? [],
      backgroundColor: "rgba(14, 98, 224, 0.8)",
      borderColor: "rgba(14, 98, 224, 1)",
      borderWidth: 1,
    },
  ];

  const open = Boolean(anchorEl);

  const downloadReport = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Helper function to convert image to base64 data URL
  const loadImageAsDataUrl = (src) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = reject;
      img.src = src;
    });
  };

  // Helper function to convert SVG to PNG data URL with explicit dimensions
  const convertSvgToPng = (svgSrc, width = 200, height = 60) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          // Draw white background for transparency
          ctx.fillStyle = "white";
          ctx.fillRect(0, 0, width, height);
          // Draw the SVG image scaled to fit
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL("image/png"));
        } else {
          reject(new Error("Could not get canvas context"));
        }
      };
      img.onerror = (e) => {
        console.error("Error loading SVG:", e);
        reject(new Error("Failed to load SVG image"));
      };
      img.src = svgSrc;
    });
  };

  // Helper function to convert all images in HTML to base64
  const convertImagesToBase64 = async (htmlContent) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlContent, "text/html");
    const images = doc.querySelectorAll("img");

    for (const img of Array.from(images)) {
      const src = img.getAttribute("src");
      if (src && !src.startsWith("data:")) {
        try {
          const dataUrl = await loadImageAsDataUrl(src);
          img.setAttribute("src", dataUrl);
        } catch (error) {
          console.warn(`Could not convert image ${src} to base64:`, error);
        }
      }
    }

    return doc.documentElement.outerHTML;
  };

  const downloadReportPdf = async () => {
    handleClose();
    try {
      const element = reportRef.current;

      if (!element) {
        console.error("Report container not found");
        return;
      }

      // Load logo image as data URL for header
      // Backend will use fallback if logoDataUrl is not provided
      let logoDataUrl;
      try {
        const logoSrc = user?.groupManager
          ? user?.groupCompanyId?.image
          : user?.company?.companyImage;
        if (logoSrc) {
          logoDataUrl = await loadImageAsDataUrl(logoSrc);
        }
      } catch (error) {
        console.warn("Could not load logo image:", error);
        // Backend will use fallback logo if this fails
      }

      // Load powered by logo as PNG data URL for backend
      let poweredByLogoDataUrl;
      try {
        poweredByLogoDataUrl = await convertSvgToPng(companyLogo, 200, 60);
      } catch (error) {
        console.error("Could not load powered by logo:", error);
      }

      // Wait for charts to fully render
      await new Promise((resolve) => setTimeout(resolve, 1500));

      // Convert all canvas elements (charts) to images before cloning
      const canvases = Array.from(element.querySelectorAll("canvas"));
      const canvasDataUrls = [];

      for (const canvas of canvases) {
        try {
          // Get canvas data URL with high quality
          const dataUrl = canvas.toDataURL("image/png", 1.0);

          // Get dimensions
          const width = canvas.width || canvas.offsetWidth || 400;
          const height = canvas.height || canvas.offsetHeight || 300;

          canvasDataUrls.push({
            dataUrl,
            width,
            height,
            styleWidth: canvas.style.width || `${width}px`,
            styleHeight: canvas.style.height || `${height}px`,
          });
        } catch (error) {
          console.warn("Could not convert canvas to image:", error);
          canvasDataUrls.push(null);
        }
      }

      // Clone the element after charts are rendered
      const clonedElement = element.cloneNode(true);

      // Replace canvas elements with img elements in cloned element
      const clonedCanvases = Array.from(
        clonedElement.querySelectorAll("canvas"),
      );
      clonedCanvases.forEach((clonedCanvas, index) => {
        const imageData = canvasDataUrls[index];
        if (imageData && imageData.dataUrl) {
          const img = document.createElement("img");
          img.setAttribute("src", imageData.dataUrl); // Use setAttribute to ensure it's in HTML
          img.style.width = imageData.styleWidth;
          img.style.height = imageData.styleHeight;
          img.style.maxWidth = "100%";
          img.style.display = "block";
          img.setAttribute("alt", "Chart");

          // Replace canvas with img
          if (clonedCanvas.parentNode) {
            clonedCanvas.parentNode.replaceChild(img, clonedCanvas);
          }
        }
      });

      // Remove download dropdown from cloned element
      const downloadDropdown =
        clonedElement.querySelector(".download-dropdown");
      if (downloadDropdown) {
        downloadDropdown.remove();
      }

      // Remove "Powered by" footer section from cloned element (for PDF only)
      // Note: This only removes from clonedElement, not the original element
      // The footer remains visible in the regular view, only removed in PDF download
      const poweredBySection =
        clonedElement.querySelector("#company_footer") ||
        clonedElement.querySelector(".company_footer");
      if (poweredBySection) {
        poweredBySection.remove();
      }

      // Convert all images in the HTML to base64 (including the chart images we just created)
      let htmlContent = clonedElement.innerHTML;
      htmlContent = await convertImagesToBase64(`<div>${htmlContent}</div>`);
      // Extract the inner HTML after conversion
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, "text/html");
      htmlContent = doc.body.innerHTML;

      // Get CSS styles from the module - matching misreport.module.css exactly
      const cssStyles = `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        
        * {
          box-sizing: border-box;
        }
        
        body {
          font-family: Inter, sans-serif;
          margin: 0;
          padding: 0;
          color: #000;
        }

        .table {
          width: 60%;
          margin-top: 1rem;
        }

        .logo {
          width: auto;
          height: 80px;
          margin-bottom: 1rem;
        }

        .page_break {
          display: block;
          height: 0;
          page-break-before: always !important;
          break-before: page !important;
          margin: 0;
          padding: 0;
        }

        .pageBreak {
          display: block;
          height: 0;
          page-break-before: always !important;
          break-before: page !important;
          margin: 0;
          padding: 0;
        }

        /* Support for CSS module hashed class names - page breaks */
        div[class*="page_break"],
        div[class*="pageBreak"],
        div[class*="page-break"] {
          display: block !important;
          height: 0 !important;
          page-break-before: always !important;
          break-before: page !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        /* Also support inline style page breaks */
        [style*="page-break-before: always"],
        [style*="pageBreakBefore: always"],
        [style*="break-before: page"],
        [style*="breakBefore: page"] {
          page-break-before: always !important;
          break-before: page !important;
        }

        .table_heading {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
          letter-spacing: 0.5px;
        }

        .table_value {
          font-family: Inter;
          font-size: 14px;
          font-weight: 400;
          line-height: 22px;
          letter-spacing: 0.5px;
        }

        .heading {
          font-family: Inter;
          font-size: 16px;
          font-weight: 700;
          line-height: 24px;
          letter-spacing: 0.5px;
        }

        .heading_bold {
          font-weight: 700 !important;
        }

        /* Support for CSS module hashed class names - heading_bold */
        [class*="heading_bold"] {
          font-weight: 700 !important;
        }

        .sub_heading {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
          letter-spacing: 0.5px;
          margin-left: 1rem;
        }

        .sub_heading_graph {
          font-family: Inter;
          font-size: 16px;
          font-weight: 600;
          line-height: 25.6px;
          color: rgba(102, 102, 102, 0.8);
        }

        .direct_emission_div {
          background-color: #fcfcfc;
          padding: 1rem;
          border-radius: 12px;
          margin-top: 1rem;
        }

        .scope_emission_div {
          margin-top: 2rem;
        }

        .vehicle_div {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .sub_sub_heading {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
          letter-spacing: 0.5px;
          color: rgba(0, 0, 0, 1);
          margin-top: 2rem;
        }

        .sub_sub_heading.heading_bold,
        [class*="sub_sub_heading"][class*="heading_bold"] {
          font-weight: 700 !important;
        }

        .sub_main_heading {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          line-height: 22px;
          letter-spacing: 0.5px;
          font-style: italic;
        }

        .company_footer {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          flex-direction: column;
          margin-top: 2rem;
        }

        .summary_heading {
          font-family: Inter;
          font-weight: 600;
          opacity: 90%;
        }

        .summary_heading.heading_bold,
        [class*="summary_heading"][class*="heading_bold"] {
          font-weight: 700 !important;
        }

        .summary_subheading {
          font-family: Inter;
          font-size: 14px;
          opacity: 70%;
          margin-top: -0.5rem;
        }

        .summary_text {
          font-family: Inter;
          font-size: 12px;
          opacity: 70%;
          line-height: 1.25rem;
          padding: 0 1rem;
          margin-bottom: 1rem;
        }

        .report_metadata_table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
        }

        .report_metadata_table tr {
          border-bottom: none;
        }

        .report_metadata_table td {
          padding: 0.25rem 0;
          text-align: left;
        }

        .summary_chart_container {
          display: flex !important;
          flex-direction: row !important;
          gap: 2rem;
          margin-top: 1.5rem;
          align-items: flex-start;
          width: 100%;
          justify-content: flex-start;
        }

        .summary_table_wrapper {
          width: 35%;
          max-width: 400px;
          min-width: 300px;
          flex-shrink: 0;
        }

        .summary_table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #ddd;
          background-color: #ffffff;
          table-layout: auto;
        }

        .summary_table thead {
          background-color: #1C486B;
        }

        .summary_table_header {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 0.5rem;
          border: 1px solid #ddd;
          background-color: #1C486B;
          color: #ffffff;
          text-align: left;
        }

        .summary_table_header:last-child {
          text-align: right;
        }

        .summary_table_cell {
          font-family: Inter;
          font-size: 14px;
          padding: 0.5rem;
          border: 1px solid #ddd;
          text-align: left;
          background-color: #ffffff;
        }

        .summary_table_cell[style*="text-align: right"] {
          text-align: right;
        }

        .summary_table tbody tr {
          background-color: #ffffff;
        }

        /* Support for CSS module hashed class names using attribute selectors */
        table[class*="summary_table"] {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #ddd;
          background-color: #ffffff;
          table-layout: auto;
        }

        table[class*="summary_table"] thead {
          background-color: #1C486B;
        }

        table[class*="summary_table"] th[class*="summary_table_header"] {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 0.5rem;
          border: 1px solid #ddd;
          background-color: #1C486B;
          color: #ffffff;
          text-align: left;
        }

        table[class*="summary_table"] th[class*="summary_table_header"]:last-child {
          text-align: right;
        }

        table[class*="summary_table"] td[class*="summary_table_cell"] {
          font-family: Inter;
          font-size: 14px;
          padding: 0.5rem;
          border: 1px solid #ddd;
          text-align: left;
          background-color: #ffffff;
        }

        table[class*="summary_table"] td[class*="summary_table_cell"][style*="text-align: right"] {
          text-align: right;
        }

        table[class*="summary_table"] tbody tr {
          background-color: #ffffff;
        }

        .pie_chart_wrapper {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          justify-content: flex-start;
          min-width: 300px;
        }

        .emission_breakdown_table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 1rem;
          background-color: #ffffff;
          border: 1px solid #ddd;
          table-layout: auto;
        }

        .emission_breakdown_table th:first-child,
        .emission_breakdown_table td:first-child {
          width: 15%;
        }

        .emission_breakdown_table th:nth-child(2),
        .emission_breakdown_table td:nth-child(2) {
          width: 60%;
        }

        .emission_breakdown_table th:nth-child(3),
        .emission_breakdown_table td:nth-child(3) {
          width: 25%;
          text-align: right;
        }

        .emission_breakdown_table thead {
          background-color: #1C486B;
        }

        .emission_table_header {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 0.75rem;
          border: 1px solid #ddd;
          background-color: #1C486B;
          color: #ffffff;
          text-align: left;
        }

        .emission_table_header:last-child {
          text-align: right;
        }

        .emission_breakdown_table tbody {
          background-color: #ffffff;
        }

        .emission_table_cell {
          font-family: Inter;
          font-size: 14px;
          padding: 0.75rem;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: middle;
          background-color: #ffffff;
        }

        .emission_breakdown_table tbody tr {
          background-color: #ffffff;
        }

        /* Additional styles for emission breakdown table using summary_table class */
        /* These styles ensure the table at line 1188 renders correctly in PDF */
        table.summary_table {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #ddd;
          background-color: #ffffff;
          table-layout: auto;
          margin-top: 1.5rem;
        }

        table.summary_table thead {
          background-color: #1C486B;
        }

        table.summary_table th.emission_table_header {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 0.75rem;
          border: 1px solid #ddd;
          background-color: #1C486B;
          color: #ffffff;
          text-align: left;
        }

        table.summary_table th.emission_table_header:last-child {
          text-align: right;
        }

        table.summary_table td.emission_table_cell {
          font-family: Inter;
          font-size: 14px;
          padding: 0.75rem;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: middle;
          background-color: #ffffff;
        }

        table.summary_table td.emission_table_cell[style*="text-align: right"] {
          text-align: right;
        }

        table.summary_table tbody tr {
          background-color: #ffffff;
        }

        /* Support for CSS module hashed class names using attribute selectors */
        table[class*="summary_table"] {
          width: 100%;
          border-collapse: collapse;
          border: 1px solid #ddd;
          background-color: #ffffff;
          table-layout: auto;
          margin-top: 1.5rem;
        }

        table[class*="summary_table"] thead {
          background-color: #1C486B;
        }

        table[class*="summary_table"] th[class*="emission_table_header"] {
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 0.75rem;
          border: 1px solid #ddd;
          background-color: #1C486B;
          color: #ffffff;
          text-align: left;
        }

        table[class*="summary_table"] th[class*="emission_table_header"]:last-child {
          text-align: right;
        }

        table[class*="summary_table"] td[class*="emission_table_cell"] {
          font-family: Inter;
          font-size: 14px;
          padding: 0.75rem;
          border: 1px solid #ddd;
          text-align: left;
          vertical-align: middle;
          background-color: #ffffff;
        }

        table[class*="summary_table"] tbody tr {
          background-color: #ffffff;
        }

        /* Additional styles for proper rendering */
        table {
          border-collapse: collapse;
          width: 100%;
        }

        ul {
          margin-left: 1.5rem;
          padding: 0;
        }

        li {
          margin-bottom: 0.5rem;
        }

        h1 {
          font-family: Inter;
          font-size: 24px;
          font-weight: 700;
          text-decoration: underline;
          margin-bottom: 1rem;
          margin-top: 0;
          text-align: left;
        }

        img {
          max-width: 100%;
          height: auto;
        }

        /* Material-UI Table overrides for PDF */
        .MuiTableContainer-root {
          margin-top: 1rem;
          background-color: #ffffff;
        }

        .MuiTable-root {
          width: 100%;
          border-collapse: collapse;
        }

        .MuiTableHead-root {
          background-color: #1C486B;
        }

        .MuiTableCell-head {
          background-color: #1C486B !important;
          color: #ffffff !important;
          font-family: Inter;
          font-size: 14px;
          font-weight: 600;
          padding: 8px;
          border: 1px solid #ddd;
        }

        .MuiTableCell-body {
          background-color: #ffffff;
          font-family: Inter;
          font-size: 14px;
          padding: 8px;
          border: 1px solid #ddd;
        }

        .MuiTableRow-root {
          background-color: #ffffff;
        }

        /* Additional page break support - ensure they work in PDF */
        div.page-break,
        div[class*="page-break"] {
          display: block !important;
          height: 0 !important;
          width: 100% !important;
          page-break-before: always !important;
          break-before: page !important;
          margin: 0 !important;
          padding: 0 !important;
          border: none !important;
        }
      `;

      // Get FULL HTML of the report component with all CSS styles
      const html = `
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            ${cssStyles}
          </style>
        </head>
        <body>
          ${htmlContent}
        </body>
      </html>
    `;

      // Get base URL and remove /api/v1 suffix for PDF generation endpoint
      const apiBaseUrl =
        sessionStorage.getItem("apiBaseUrl") ||
        api ||
        "https://uat-api.nirantara.ai";
      const baseUrl = apiBaseUrl.replace(/\/api\/v1$/, "");

      const response = await fetch(`${baseUrl}/generate-pdf`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          html,
          logoDataUrl: logoDataUrl || undefined, // Send undefined if not available, backend will use fallback
          skipFirstPageHeader: true,
          poweredByLogoDataUrl: poweredByLogoDataUrl || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate PDF");
      }

      // Convert response to Blob and download
      const pdfBlob = await response.blob();
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "GHG_Report.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("PDF download error:", error);
    }
  };

  const downloadReportWord = () => {
    handleClose();
    const input = reportRef.current;
    if (!input) {
      console.error("❌ reportRef is null");
      return;
    }

    const pageBreakHTML = "<br style='page-break-before: always;'>";

    // Clone innerHTML to manipulate it
    let htmlContent = input.innerHTML;

    // Add page break before large tables (scrollHeight > 800)
    const tables = input.querySelectorAll("table");
    tables.forEach((table) => {
      if (table.scrollHeight > 800) {
        htmlContent = htmlContent.replace(
          table.outerHTML,
          pageBreakHTML + table.outerHTML,
        );
      }
    });

    // Add page break before scope headings
    htmlContent = htmlContent.replace(
      /(<h3>Scope \d+:.*?<\/h3>)/g,
      `${pageBreakHTML}$1`,
    );

    const header = `
      <html xmlns:o='urn:schemas-microsoft-com:office:office'
            xmlns:w='urn:schemas-microsoft-com:office:word'
            xmlns='http://www.w3.org/TR/REC-html40'>
        <head>
          <meta charset='utf-8'>
          <title>Document</title>
          <style>
            table {
              font-size: 12px;
              border-collapse: collapse;
              width: 100%;
              page-break-inside: avoid; /* Avoid splitting table */
            }
            thead {
              display: table-header-group;
            }
            tfoot {
              display: table-footer-group;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            td, th {
              border: 1px solid #000;
              padding: 4px;
              text-align: left;
            }
          </style>
        </head>
        <body>
    `;

    const footer = "</body></html>";

    const sourceHTML = header + htmlContent + footer;

    const blob = new Blob(["\ufeff", sourceHTML], {
      type: "application/msword",
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "GHG_Report.doc";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: "1.5rem",
        }}
      >
        <p
          style={{
            fontFamily: "Inter",
            fontSize: "22px",
            fontWeight: "700",
          }}
        >
          GHG Report Details
        </p>
        <Button
          startIcon={<DownloadIcon />}
          variant="contained"
          sx={{
            backgroundColor: "#1C486B",
            padding: "0.5rem 1rem",
            borderRadius: "4px",
            height: "40px",
            textTransform: "none",
          }}
          onClick={downloadReport}
        >
          Download Report
        </Button>
        <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
          <MenuItem onClick={downloadReportPdf}>Download as PDF</MenuItem>
          <MenuItem onClick={downloadReportWord}>Download as Word</MenuItem>
        </Menu>
      </div>

      <div ref={reportRef} style={{ padding: "1rem 0" }}>
        {/* Header Section */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: "2rem",
          }}
        >
          <div style={{ flex: 1 }}>
            <h1
              style={{
                fontFamily: "Inter",
                fontSize: "24px",
                fontWeight: "700",
                textDecoration: "underline",
                marginBottom: "1rem",
                marginTop: 0,
                textAlign: "left",
              }}
            >
              Greenhouse Gas Inventory Report
            </h1>
            <table className={styles.report_metadata_table}>
              <tr>
                <td className={styles.table_heading}>Company Name:</td>
                <td className={styles.table_value}>
                  {user?.company?.companyName || "-"}
                </td>
              </tr>
              <tr>
                <td className={styles.table_heading}>Inventory Year:</td>
                <td className={styles.table_value}>
                  {data?.reportingPeriod === "CalendarYear"
                    ? `${data?.reportingYear ?? "YYYY"} - ${data?.reportingYear ?? "YYYY"}`
                    : `Apr ${data?.reportingYear ?? "YYYY"} - Mar ${data?.reportingYear + 1 ?? "YYYY"}`}
                </td>
              </tr>
              <tr>
                <td className={styles.table_heading}>Prepared By:</td>
                <td className={styles.table_value}>
                  {data?.preparedBy?.name || "-"}
                </td>
              </tr>
              <tr>
                <td className={styles.table_heading}>
                  Date of report generation:
                </td>
                <td className={styles.table_value}>
                  {data?.updatedAt
                    ? new Date(data?.updatedAt).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })
                    : new Date().toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      })}
                </td>
              </tr>
            </table>
          </div>
          <div style={{ flex: 1, textAlign: "right" }}>
            <img
              src={
                user?.groupManager
                  ? user?.groupCompanyId?.image
                  : user?.company?.companyImage
              }
              alt="Company Logo"
              style={{ width: "120px", height: "auto" }}
            />
          </div>
        </div>

        {/* Executive Summary Section */}
        <div style={{ marginTop: "2rem" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            1. Executive Summary
          </p>
          <div style={{ marginTop: "0.5rem" }}>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.overallPerformance ||
                `This report addresses the commitment of ${user?.company?.companyName || "the company"} towards measuring GHG emissions and towards ${user?.company?.companyName || "the company"}'s road to sustainability.`}
            </p>

            <p
              className={`${styles.summary_heading} heading_bold`}
              style={{ marginTop: "1rem", marginBottom: "0.5rem" }}
            >
              Key findings:
            </p>
            <ul style={{ marginLeft: "1.5rem", padding: 0 }}>
              {data?.executiveSummary?.keyFindings ? (
                data.executiveSummary.keyFindings
                  .split("\n")
                  .filter((item) => item.trim() !== "")
                  .map((finding, index) => (
                    <li
                      key={index}
                      className={styles.summary_subheading}
                      style={{ marginBottom: "0.5rem" }}
                    >
                      {finding.trim()}
                    </li>
                  ))
              ) : (
                <>
                  <li
                    className={styles.summary_subheading}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    Total emissions:{" "}
                    {truncateNumber(
                      (data?.scope1Emissions?.totalScope1Combustion
                        ?.thisMonthTotal ?? 0) +
                        (data?.scope2Emissions?.totalEmission?.thisMonthTotal ??
                          0) +
                        (data?.scope3Emissions?.totalEmission?.thisMonthTotal ??
                          0),
                    )}{" "}
                    tCO2e
                  </li>
                  <li
                    className={styles.summary_subheading}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    Scope 1:{" "}
                    {truncateNumber(
                      data?.scope1Emissions?.totalScope1Combustion
                        ?.thisMonthTotal ?? 0,
                    )}{" "}
                    tCO2e (
                    {(
                      ((data?.scope1Emissions?.totalScope1Combustion
                        ?.thisMonthTotal ?? 0) /
                        Math.max(
                          (data?.scope1Emissions?.totalScope1Combustion
                            ?.thisMonthTotal ?? 0) +
                            (data?.scope2Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0) +
                            (data?.scope3Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0),
                          1,
                        )) *
                      100
                    ).toFixed(1)}
                    %)
                  </li>
                  <li
                    className={styles.summary_subheading}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    Scope 2:{" "}
                    {truncateNumber(
                      data?.scope2Emissions?.totalEmission?.thisMonthTotal ?? 0,
                    )}{" "}
                    tCO2e (
                    {(
                      ((data?.scope2Emissions?.totalEmission?.thisMonthTotal ??
                        0) /
                        Math.max(
                          (data?.scope1Emissions?.totalScope1Combustion
                            ?.thisMonthTotal ?? 0) +
                            (data?.scope2Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0) +
                            (data?.scope3Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0),
                          1,
                        )) *
                      100
                    ).toFixed(1)}
                    %)
                  </li>
                  <li
                    className={styles.summary_subheading}
                    style={{ marginBottom: "0.5rem" }}
                  >
                    Scope 3:{" "}
                    {truncateNumber(
                      data?.scope3Emissions?.totalEmission?.thisMonthTotal ?? 0,
                    )}{" "}
                    tCO2e (
                    {(
                      ((data?.scope3Emissions?.totalEmission?.thisMonthTotal ??
                        0) /
                        Math.max(
                          (data?.scope1Emissions?.totalScope1Combustion
                            ?.thisMonthTotal ?? 0) +
                            (data?.scope2Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0) +
                            (data?.scope3Emissions?.totalEmission
                              ?.thisMonthTotal ?? 0),
                          1,
                        )) *
                      100
                    ).toFixed(1)}
                    %)
                  </li>
                </>
              )}
            </ul>

            <p
              className={`${styles.summary_heading} heading_bold`}
              style={{ marginTop: "1rem", marginBottom: "0.5rem" }}
            >
              Emission trends:
            </p>
            <ul style={{ marginLeft: "1.5rem", padding: 0 }}>
              <li className={styles.summary_subheading}>
                {data?.executiveSummary?.baselineComparison ||
                  `Comparing ${data?.reportingYear || "current"} inventory year with previous inventory year, emissions have shown ${data?.scope1Emissions?.totalScope1Combustion?.percentageChange > 0 ? "an increase" : "a decrease"} of ${Math.abs(data?.scope1Emissions?.totalScope1Combustion?.percentageChange ?? 0).toFixed(1)}% in Scope 1 emissions.`}
              </li>
            </ul>
          </div>
        </div>

        {/* GHG Emission Factor Section */}
        <div style={{ marginTop: "2rem" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            2. GHG Emission Factor
          </p>
          <div style={{ marginTop: "0.5rem" }}>
            <p className={styles.summary_subheading}>
              {data?.ghgEmissionFactor ||
                "This report uses DEFRA (from UK govt) and IPCC Global warming potential for calculation of emissions. GHG protocol standard is following as part of methodology."}
            </p>
          </div>
        </div>

        {/* GHG Reporting Section */}
        {/* <PageBreak /> */}
        <div style={{ marginTop: "2rem", width: "100%" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            3. GHG Reporting
          </p>
          <div className={styles.direct_emission_div}>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.ghgReporting ||
                `Interpret the GHG emission of ${user?.company?.companyName || "[company]"} in terms of total emissions and percentage. Consider scope 1, scope 2, and scope 3 and their breakdowns: stationary combustion, mobile combustion, fugitive emissions, purchased electricity, heating and cooling, steam, purchased goods and services, upstream transportation and distribution, waste generated in operations, business travel, employee commute and downstream transportation and distribution.`}
            </p>

            {/* Detailed Emission Breakdown Table */}
            <table
              className={styles.summary_table}
              style={{ marginTop: "1.5rem", width: "100%" }}
            >
              <thead>
                <tr>
                  <th className={styles.emission_table_header}>
                    Emission Scope
                  </th>
                  <th className={styles.emission_table_header}>
                    Emission Source
                  </th>
                  <th className={styles.emission_table_header}>
                    Total Emission (tCO2e)
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Scope 1 */}
                <tr>
                  <td
                    className={styles.emission_table_cell}
                    rowSpan="3"
                    style={{ verticalAlign: "middle" }}
                  >
                    Scope 1
                  </td>
                  <td className={styles.emission_table_cell}>
                    Stationary Combustion
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope1Emissions?.stationaryCombustion
                        ?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Mobile Combustion
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope1Emissions?.mobileCombustion?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Fugitive Emission
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope1Emissions?.fugitiveCombustion
                        ?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                {/* Scope 2 */}
                <tr>
                  <td
                    className={styles.emission_table_cell}
                    rowSpan="3"
                    style={{ verticalAlign: "middle" }}
                  >
                    Scope 2
                  </td>
                  <td className={styles.emission_table_cell}>
                    Purchased Electricity
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Purchased Heating and Cooling
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope2Emissions?.heat?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Purchased Steam
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope2Emissions?.steam?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                {/* Scope 3 */}
                <tr>
                  <td
                    className={styles.emission_table_cell}
                    rowSpan="6"
                    style={{ verticalAlign: "middle" }}
                  >
                    Scope 3
                  </td>
                  <td className={styles.emission_table_cell}>
                    Purchased Goods & Services
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Upstream Transportation and Distribution
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.upstreamTransportation
                        ?.thisMonthTotal ??
                        data?.scope3Emissions?.upstreamAndDownStream
                          ?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Waste Generated in operations
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Business Travel
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.businessTravel?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Employee Commute
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.employeeCommute?.thisMonthTotal ??
                        0,
                    )}
                  </td>
                </tr>
                <tr>
                  <td className={styles.emission_table_cell}>
                    Downstream Transportation and Distribution
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ textAlign: "right" }}
                  >
                    {truncateNumber(
                      data?.scope3Emissions?.downstreamTransportation
                        ?.thisMonthTotal ?? 0,
                    )}
                  </td>
                </tr>
                {/* Total */}
                <tr>
                  <td
                    className={styles.emission_table_cell}
                    colSpan="2"
                    style={{ fontWeight: "600" }}
                  >
                    Total Emissions
                  </td>
                  <td
                    className={styles.emission_table_cell}
                    style={{ fontWeight: "600", textAlign: "right" }}
                  >
                    {truncateNumber(
                      Number(
                        data?.scope1Emissions?.totalScope1Combustion
                          ?.thisMonthTotal ?? 0,
                      ) +
                        Number(
                          data?.scope2Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        ) +
                        Number(
                          data?.scope3Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        ),
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {/* Summary Table and Pie Chart Layout */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: "2rem",
                alignItems: "flex-start",
                width: "100%",
                marginTop: "1.5rem",
                marginBottom: "1.5rem",
              }}
            >
              {/* Summary Table - Left Side */}
              <div
                style={{
                  width: "45%",
                  flexShrink: 0,
                  marginTop: "1.5rem",
                }}
              >
                <table
                  className={styles.summary_table}
                  style={{ width: "100%", display: "table" }}
                >
                  <thead>
                    <tr>
                      <th className={styles.summary_table_header}>
                        {user?.company?.companyName || "[Company name]"}
                      </th>
                      <th className={styles.summary_table_header}>
                        Emission (tCO2e)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.summary_table_cell}>Scope 1</td>
                      <td
                        className={styles.summary_table_cell}
                        style={{ textAlign: "right" }}
                      >
                        {truncateNumber(
                          data?.scope1Emissions?.totalScope1Combustion
                            ?.thisMonthTotal ?? 0,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.summary_table_cell}>Scope 2</td>
                      <td
                        className={styles.summary_table_cell}
                        style={{ textAlign: "right" }}
                      >
                        {truncateNumber(
                          data?.scope2Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.summary_table_cell}>Scope 3</td>
                      <td
                        className={styles.summary_table_cell}
                        style={{ textAlign: "right" }}
                      >
                        {truncateNumber(
                          data?.scope3Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.summary_table_cell}>Scope 1 + 2</td>
                      <td
                        className={styles.summary_table_cell}
                        style={{ textAlign: "right" }}
                      >
                        {truncateNumber(
                          Number(
                            data?.scope1Emissions?.totalScope1Combustion
                              ?.thisMonthTotal ?? 0,
                          ) +
                            Number(
                              data?.scope2Emissions?.totalEmission
                                ?.thisMonthTotal ?? 0,
                            ),
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className={styles.summary_table_cell}>
                        Scope 1 + 2 + 3
                      </td>
                      <td
                        className={styles.summary_table_cell}
                        style={{ textAlign: "right" }}
                      >
                        {truncateNumber(
                          Number(
                            data?.scope1Emissions?.totalScope1Combustion
                              ?.thisMonthTotal ?? 0,
                          ) +
                            Number(
                              data?.scope2Emissions?.totalEmission
                                ?.thisMonthTotal ?? 0,
                            ) +
                            Number(
                              data?.scope3Emissions?.totalEmission
                                ?.thisMonthTotal ?? 0,
                            ),
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Pie Chart - Right Side */}
              <div
                style={{
                  flex: "1",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  marginTop: "1rem",
                }}
              >
                <PieChart
                  labels={["Scope 1", "Scope 2", "Scope 3"]}
                  dataset={[
                    {
                      data: [
                        Number(
                          data?.scope1Emissions?.totalScope1Combustion
                            ?.thisMonthTotal ?? 0,
                        ),
                        Number(
                          data?.scope2Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        ),
                        Number(
                          data?.scope3Emissions?.totalEmission
                            ?.thisMonthTotal ?? 0,
                        ),
                      ],
                      backgroundColor: ["#FF4C4C", "#007BFF", "#eb7e0e"],
                      borderColor: "#ffffff",
                      borderWidth: 2,
                    },
                  ]}
                  customWidth={325}
                  customHeight={270}
                  displayLegend={true}
                  showOutsidePercentages={true}
                />
                <p
                  style={{
                    fontFamily: "Inter",
                    fontSize: "12px",
                    marginBottom: "1rem",
                    fontStyle: "italic",
                  }}
                >
                  Title: {user?.company?.companyName || "[Company name]"}: GHG
                  Emissions - {data?.reportingYear || "[inventory year]"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Scope 1 Section */}

        <div style={{ marginTop: "2rem" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            4. Scope 1: Direct Emissions
          </p>
          <div className={styles.direct_emission_div}>
            <CustomTable headers={scope1Headers} rows={scope1Rows} />
            <p className={styles.summary_text} style={{ marginTop: "1rem" }}>
              {data?.executiveSummary?.scope1Summary || null}
            </p>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: "600",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Stack Bar graph comparing current and previous inventory year
            </p>
            <div style={{ marginTop: "1rem" }}>
              <BarChart
                labels={[
                  "Stationary Combustion",
                  "Mobile Combustion",
                  "Fugitive Emission",
                ]}
                datasets={[
                  {
                    label: `${data?.reportingYear || new Date().getFullYear()} Inventory Year`,
                    data: [
                      Number(
                        data?.scope1Emissions?.stationaryCombustion
                          ?.thisMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope1Emissions?.mobileCombustion
                          ?.thisMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope1Emissions?.fugitiveCombustion
                          ?.thisMonthTotal ?? 0,
                      ),
                    ],
                    backgroundColor: "rgba(255, 79, 79, 0.8)",
                    borderColor: "rgba(255, 79, 79, 1)",
                    barThickness: 30,
                  },
                  {
                    label: `${Number(data?.reportingYear || new Date().getFullYear()) - 1} Inventory Year`,
                    data: [
                      Number(
                        data?.scope1Emissions?.stationaryCombustion
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope1Emissions?.mobileCombustion
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope1Emissions?.fugitiveCombustion
                          ?.previousMonthTotal ?? 0,
                      ),
                    ],
                    backgroundColor: "rgba(14, 98, 224, 0.8)",
                    borderColor: "rgba(14, 98, 224, 1)",
                    barThickness: 30,
                  },
                ]}
                ellipsis={false}
                display={true}
                yAxisTitle={"Scope 1 Emission (tCO2e)"}
              />
            </div>
          </div>
        </div>

        {/* Scope 2 Section */}
        {/* <PageBreak /> */}
        <div style={{ marginTop: "2rem" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            5. Scope 2: Indirect Emissions from purchased energy
          </p>
          <div className={styles.direct_emission_div}>
            <CustomTable headers={scope1Headers} rows={scope2Rows} />
            <p className={styles.summary_text} style={{ marginTop: "1rem" }}>
              {data?.executiveSummary?.scope2Summary || null}
            </p>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: "600",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Stack Bar graph comparing current and previous inventory year
            </p>
            <div style={{ marginTop: "1rem" }}>
              <BarChart
                labels={["Electricity", "Heating and Cooling", "Steam"]}
                datasets={[
                  {
                    label: `${data?.reportingYear || new Date().getFullYear()} Inventory Year`,
                    data: [
                      Number(
                        data?.scope2Emissions?.electricity?.thisMonthTotal ?? 0,
                      ),
                      Number(data?.scope2Emissions?.heat?.thisMonthTotal ?? 0),
                      Number(data?.scope2Emissions?.steam?.thisMonthTotal ?? 0),
                    ],
                    backgroundColor: "rgba(255, 79, 79, 0.8)",
                    borderColor: "rgba(255, 79, 79, 1)",
                    barThickness: 30,
                  },
                  {
                    label: `${Number(data?.reportingYear || new Date().getFullYear()) - 1} Inventory Year`,
                    data: [
                      Number(
                        data?.scope2Emissions?.electricity
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope2Emissions?.heat?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope2Emissions?.steam?.previousMonthTotal ?? 0,
                      ),
                    ],
                    backgroundColor: "rgba(14, 98, 224, 0.8)",
                    borderColor: "rgba(14, 98, 224, 1)",
                    barThickness: 30,
                  },
                ]}
                ellipsis={false}
                display={true}
                yAxisTitle={"Scope 2 Emission (tCO2e)"}
              />
            </div>
          </div>
        </div>

        {/* Scope 3 Section */}
        {/* <PageBreak /> */}
        <div style={{ marginTop: "2rem" }}>
          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            6. Scope 3: Emissions from Value Chain
          </p>
          <div className={styles.direct_emission_div}>
            <CustomTable headers={scope1Headers} rows={scope3Rows} />
            <p className={styles.summary_text} style={{ marginTop: "1rem" }}>
              {data?.executiveSummary?.scope3Summary || null}
            </p>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: "600",
                marginTop: "0.5rem",
                fontStyle: "italic",
              }}
            >
              Stack Bar graph comparing current and previous inventory year
            </p>
            <div style={{ marginTop: "1rem" }}>
              <BarChart
                labels={[
                  "Purchased Goods",
                  "Upstream Transportation",
                  "Waste Generated",
                  "Business Travel",
                  "Employee Commute",
                  "Downstream Transportation",
                ]}
                datasets={[
                  {
                    label: `${data?.reportingYear || new Date().getFullYear()} Inventory Year`,
                    data: [
                      Number(
                        data?.scope3Emissions?.purchasedGoods?.thisMonthTotal ??
                          0,
                      ),
                      Number(
                        data?.scope3Emissions?.upstreamTransportation
                          ?.thisMonthTotal ??
                          data?.scope3Emissions?.upstreamAndDownStream
                            ?.thisMonthTotal ??
                          0,
                      ),
                      Number(
                        data?.scope3Emissions?.wasteGenerated?.thisMonthTotal ??
                          0,
                      ),
                      Number(
                        data?.scope3Emissions?.businessTravel?.thisMonthTotal ??
                          0,
                      ),
                      Number(
                        data?.scope3Emissions?.employeeCommute
                          ?.thisMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope3Emissions?.downstreamTransportation
                          ?.thisMonthTotal ?? 0,
                      ),
                    ],
                    backgroundColor: "rgba(255, 79, 79, 0.8)",
                    borderColor: "rgba(255, 79, 79, 1)",
                    barThickness: 30,
                  },
                  {
                    label: `${Number(data?.reportingYear || new Date().getFullYear()) - 1} Inventory Year`,
                    data: [
                      Number(
                        data?.scope3Emissions?.purchasedGoods
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope3Emissions?.upstreamTransportation
                          ?.previousMonthTotal ??
                          data?.scope3Emissions?.upstreamAndDownStream
                            ?.previousMonthTotal ??
                          0,
                      ),
                      Number(
                        data?.scope3Emissions?.wasteGenerated
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope3Emissions?.businessTravel
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope3Emissions?.employeeCommute
                          ?.previousMonthTotal ?? 0,
                      ),
                      Number(
                        data?.scope3Emissions?.downstreamTransportation
                          ?.previousMonthTotal ?? 0,
                      ),
                    ],
                    backgroundColor: "rgba(14, 98, 224, 0.8)",
                    borderColor: "rgba(14, 98, 224, 1)",
                    barThickness: 30,
                  },
                ]}
                ellipsis={false}
                display={true}
                yAxisTitle={"Scope 3 Emission (tCO2e)"}
              />
            </div>
          </div>
        </div>

        {/* Facility-wise Analytics Section */}
        {data?.typeOfReport === "Consolidated" ? (
          <div style={{ marginTop: "2rem" }}>
            {/* <PageBreak /> */}
            <p
              className={`${styles.heading} heading_bold`}
              style={{ textAlign: "left" }}
            >
              7. Facility-wise Analytics
            </p>
          {console.log(data?.scope1Summary)}
            <div className={styles.direct_emission_div}>
              <div>
                <p className={styles.sub_sub_heading}>
                  bar chart showing Scope 1 of current and previous months
                  emissions
                </p>
                <BarChart
                  labels={
                    scope1BarChartData &&
                    scope1BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope1Data}
                  display={true}
                  yAxisTitle={"Scope 1 Emission (tCO2e)"}
                />
                <p className={styles.summary_text}>
                  {data?.scope1Summary || summary?.scope1 || null}
                </p>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <p className={styles.sub_sub_heading}>
                  bar chart showing Scope 2 of current and previous emissions
                </p>
                <BarChart
                  labels={
                    scope2BarChartData &&
                    scope2BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope2Data}
                  display={true}
                  yAxisTitle={"Scope 2 Emission (tCO2e)"}
                />
                <p className={styles.summary_text}>
                  {data?.scope2Summary || summary?.scope2 || null}
                </p>
              </div>

              <div style={{ marginTop: "2rem" }}>
                <p className={styles.sub_sub_heading}>
                  bar chart showing Scope 3 of current and previous emissions
                </p>
                <BarChart
                  labels={
                    scope3BarChartData &&
                    scope3BarChartData?.map((item) => item?.facility)
                  }
                  datasets={scope3Data}
                  display={true}
                  yAxisTitle={"Scope 3 Emission (tCO2e)"}
                />
                <p className={styles.summary_text}>
                  {data?.scope3Summary || summary?.scope3 || null}
                </p>
              </div>
            </div>
          </div>
        ) : null}

        {/* Conclusion Section */}
        <div style={{ marginTop: "2rem" }}>
          {/* <PageBreak /> */}

          <p
            className={`${styles.heading} heading_bold`}
            style={{ textAlign: "left" }}
          >
            {data?.typeOfReport === "Consolidated" ? "8" : "7"}. Conclusion
          </p>

          <div>
            <p
              className={`${styles.sub_sub_heading} heading_bold`}
              style={{ marginTop: "1rem" }}
            >
              Summary of key findings
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.summaryOfFindings ||
                `Summarize major observations across GHG accounting – scope 1,2,3 matrices of ${user?.company?.companyName || "the company"}, and comparison between these matrices amongst the facilities. Ensure to highlight key findings, mention, if any, alarming matrices / hotspot. In the summary make use of numbers along the units and percentages.`}
            </p>
            <p
              className={`${styles.sub_sub_heading} heading_bold`}
              style={{ marginTop: "1rem" }}
            >
              Recommendations for the upcoming year
            </p>
            <p className={styles.summary_subheading}>
              {data?.executiveSummary?.recommendations ||
                `Recommend specific actions or focus areas to reduce GHG Emission in the coming year for ${user?.company?.companyName || "the company"}.`}
            </p>
          </div>
        </div>

        {/* Note Section */}
        <div style={{ marginTop: "2rem" }}>
          <p
            style={{
              fontFamily: "Inter",
              fontSize: "12px",
              fontStyle: "italic",
              opacity: 0.7,
            }}
          >
            Note: All values will be auto-filled and validated by the platform
            using AI to ensure accuracy and consistency in reporting.
          </p>
        </div>

        <div style={{ marginTop: "3rem" }}>
          <p className={`${styles.heading} heading_bold`}>
            {/* <li className={"heading_bold"}>Appendices</li> */}
            Appendices
          </p>

          <div style={{ marginLeft: "1rem" }}>
            <p
              className={`${styles.sub_main_heading} `}
              style={{ fontStyle: "normal" }}
            >
              <span className={`${styles.appendix_label} heading_bold`}>
                Appendix A: Emission Source
              </span>
            </p>
            <ol
              className={styles.appendix_text}
              style={{ marginTop: "0.25rem" }}
            >
              {Array.isArray(data?.sources) && data.sources.length > 0 ? (
                data.sources.map((source, index) => (
                  <li key={`${source}-${index}`}>{source}</li>
                ))
              ) : Array.isArray(data) &&
                Array.isArray(data[0]?.sources) &&
                data[0].sources.length > 0 ? (
                data[0].sources.map((source, index) => (
                  <li key={`${source}-${index}`}>{source}</li>
                ))
              ) : (
                <li></li>
              )}
            </ol>

            {/* <p className={`${styles.sub_main_heading} `} style={{ fontStyle: "normal" }}>
              <span className={`${styles.appendix_label} heading_bold`}>Appendix D:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixD || "Detailed Board and Governance Data"}
              </span>
            </p> */}
            {/* <p className={styles.sub_main_heading} style={{ fontStyle: "normal" }}>
              <span className={styles.appendix_label}>Appendix E:</span>{" "}
              <span className={styles.appendix_text}>
                {dataSource?.appendixE || "Emission Datasets source- Defra"}
              </span>
            </p> */}
          </div>
        </div>
        <div className={styles.company_footer} id="company_footer">
          <img src={companyLogo} alt="logo" width={150} />
          <p className={styles.sub_sub_heading}>Report Powered by Nirantara</p>
        </div>
      </div>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={loading}
      >
        <CircularProgress color="inherit" />
        <Typography sx={{ mt: 2 }}>
          We are comparing GHG Report. Please wait...
        </Typography>
      </Backdrop>
    </>
  );
};

export default GhgReport;
